import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  getAllMedicalRecords,
  getMedicalRecordById,
} from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import { RoleGuard } from "../../../components/RBAC";

export const MedicalRecords = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const { data, isLoading } = useQuery(
    [
      "medical-records",
      {
        search: search,
        limit: limit,
        offset: limit * page,
      },
    ],
    getAllMedicalRecords
  );

  console.log(JSON.stringify(data?.data));

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (record: any) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const Columns = [
    {
      title: "Patient Name",
      dataIndex: "patient",
      key: "patient",
      render: (colData: any) => colData?.fullName || "N/A",
    },
    {
      title: "Session ID",
      dataIndex: "sessionId",
      key: "sessionId",
    },
    {
      title: "Record Type",
      dataIndex: "recordType",
      key: "recordType",
      render: (colData: any) => (
        <Chip label={colData || "General"} size="small" />
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (colData: any) => formatDate(colData),
    },
    {
      title: "Clinician",
      dataIndex: "clinician",
      key: "clinician",
      render: (colData: any) => colData?.fullName || "N/A",
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <Box display="flex" gap="20px">
          <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewClick(row)}
            >
              <VisibilityIcon />
            </IconButton>
          </RoleGuard>
        </Box>
      ),
    },
  ];

  return (
    <Box my={2}>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Typography variant="h3">Medical Records</Typography>
      </Box>
      <Box>
        <TextField
          label="Search by patient name or session ID"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>
      <Box>
        {data?.data?.count === 0 ? (
          <EmptyScreen type="medical records" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box mt={2}>
                <Table
                  key={"medical-records"}
                  dataSource={data?.data?.data}
                  rowKey={"id"}
                  columns={Columns}
                />
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={data?.data?.count || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Medical Record Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Patient Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.patient?.fullName || "N/A"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Clinician
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.clinician?.fullName || "N/A"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Session ID
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.sessionId}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Record Type
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.recordType || "General"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.notes || "No notes available"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Diagnosis
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.diagnosis || "No diagnosis recorded"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Prescription
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.prescription || "No prescription"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedRecord.createdAt)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
